import type { RxDatabase } from "rxdb";
import { RXDB_AMP_COLLECTION_NAME } from "../collections/amp";
import { RXDB_CLINIC_INSURANCE_COLLECTION_NAME } from "../collections/clinic-insurance";
import { RXDB_CLINICAL_PRODUCT_CATEGORY_COLLECTION_NAME } from "../collections/clinical-product-category";
import { RXDB_CLINICAL_PRODUCT_FORM_COLLECTION_NAME } from "../collections/clinical-product-form";
import { RXDB_CONSULTATION_COLLECTION_NAME } from "../collections/consultation";
import { RXDB_DISEASE_COLLECTION_NAME } from "../collections/disease";
import { RXDB_DISPENSATION_COLLECTION_NAME } from "../collections/dispensation";
import { RXDB_EPOCUS_BILLING_COLLECTION_NAME } from "../collections/epocus-billing";
import { RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME } from "../collections/epocus-consent-document";
import { RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME } from "../collections/epocus-encounter";
import { RXDB_EPOCUS_EXAM_COLLECTION_NAME } from "../collections/epocus-exam";
import { RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME } from "../collections/epocus-patient-consent";
import { RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME } from "../collections/epocus-probe-association";
import { RXDB_EPOCUS_PROGRAM_COLLECTION_NAME } from "../collections/epocus-program";
import { RXDB_EPOCUS_REVIEW_COLLECTION_NAME } from "../collections/epocus-review";
import { RXDB_EXCHANGE_RATE_COLLECTION_NAME } from "../collections/exchange-rate";
import { RXDB_FACILITY_COLLECTION_NAME } from "../collections/facility";
import { RXDB_IMAGING_INSTANCE_COLLECTION_NAME } from "../collections/imaging-instance";
import { RXDB_IMAGING_STUDY_COLLECTION_NAME } from "../collections/imaging-study";
import { RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME } from "../collections/investigation-activity";
import { RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME } from "../collections/investigation-catalogue-item";
import { RXDB_INVOICE_COLLECTION_NAME } from "../collections/invoice";
import { RXDB_NON_PHARMA_ITEM_COLLECTION_NAME } from "../collections/non-pharma-item";
import { RXDB_NOTIFICATION_BELL_STATE_COLLECTION_NAME } from "../collections/notification-bell-state";
import { RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME } from "../collections/notification-dismissal";
import { RXDB_PATIENT_COLLECTION_NAME } from "../collections/patient";
import { RXDB_PRESCRIPTION_COLLECTION_NAME } from "../collections/prescription";
import { RXDB_PRICE_COLLECTION_NAME } from "../collections/price";
import { RXDB_PRICE_ACKNOWLEDGEMENT_COLLECTION_NAME } from "../collections/price-acknowledgement";
import { RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME } from "../collections/price-list-facility";
import { RXDB_PRICE_LIST_ITEM_COLLECTION_NAME } from "../collections/price-list-item";
import { RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME } from "../collections/price-list-item-external-reference";
import { RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME } from "../collections/price-list-item-name";
import { RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME } from "../collections/price-list-revenue-department";
import { RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME } from "../collections/price-list-tariff";
import { RXDB_REFERRAL_COLLECTION_NAME } from "../collections/referral";
import { RXDB_REFERRAL_FEEDBACK_COLLECTION_NAME } from "../collections/referral-feedback";
import { RXDB_SYMPTOM_COLLECTION_NAME } from "../collections/symptom";
import { RXDB_VMP_COLLECTION_NAME } from "../collections/vmp";
import { RXDB_VTM_COLLECTION_NAME } from "../collections/vtm";
import type { ReplicationState, RxDBCollections } from "../provider";
import { useReplicateAmp } from "../replication/amp";
import { useReplicateClinicInsurance } from "../replication/clinic-insurance";
import { useReplicateClinicalProductCategory } from "../replication/clinical-product-category";
import { useReplicateClinicalProductForm } from "../replication/clinical-product-form";
import { useReplicateConsultation } from "../replication/consultation";
import { useReplicateDisease } from "../replication/disease";
import { useReplicateDispensation } from "../replication/dispensation";
import { useReplicateEpocusBilling } from "../replication/epocus-billing";
import { useReplicateEpocusConsentDocument } from "../replication/epocus-consent-document";
import { useReplicateEpocusEncounter } from "../replication/epocus-encounter";
import { useReplicateEpocusExam } from "../replication/epocus-exam";
import { useReplicateEpocusPatientConsent } from "../replication/epocus-patient-consent";
import { useReplicateEpocusProbeAssociation } from "../replication/epocus-probe-association";
import { useReplicateEpocusProgram } from "../replication/epocus-program";
import { useReplicateEpocusReview } from "../replication/epocus-review";
import { useReplicateExchangeRate } from "../replication/exchange-rate";
import { useReplicateFacility } from "../replication/facility";
import { useReplicateImagingInstance } from "../replication/imaging-instance";
import { useReplicateImagingStudy } from "../replication/imaging-study";
import { useReplicateInvestigationActivity } from "../replication/investigation-activity";
import { useReplicateInvestigationCatalogueItem } from "../replication/investigation-catalogue-item";
import { useReplicateInvoice } from "../replication/invoice";
import { useReplicateNonPharmaItem } from "../replication/non-pharma-item";
import { useReplicateNotificationBellState } from "../replication/notification-bell-state";
import { useReplicateNotificationDismissal } from "../replication/notification-dismissal";
import { useReplicatePatient } from "../replication/patient";
import { useReplicatePrescription } from "../replication/prescription";
import { useReplicatePrice } from "../replication/price";
import { useReplicatePriceAcknowledgement } from "../replication/price-acknowledgement";
import { useReplicatePriceListFacility } from "../replication/price-list-facility";
import { useReplicatePriceListItem } from "../replication/price-list-item";
import { useReplicatePriceListItemExternalReference } from "../replication/price-list-item-external-reference";
import { useReplicatePriceListItemName } from "../replication/price-list-item-name";
import { useReplicatePriceListRevenueDepartment } from "../replication/price-list-revenue-department";
import { useReplicatePriceListTariff } from "../replication/price-list-tariff";
import { useReplicateReferral } from "../replication/referral";
import { useReplicateReferralFeedback } from "../replication/referral-feedback";
import { useReplicateSymptom } from "../replication/symptom";
import { useReplicateVmp } from "../replication/vmp";
import { useReplicateVtm } from "../replication/vtm";

export const useRxdbReplication = (
  db: RxDatabase<RxDBCollections>,
): ReplicationState => {
  const patient = useReplicatePatient({
    db,
    collection: db?.[RXDB_PATIENT_COLLECTION_NAME],
  });

  const investigationActivity = useReplicateInvestigationActivity({
    db,
    collection: db?.[RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME],
  });

  const investigationCatalogueItem = useReplicateInvestigationCatalogueItem({
    db,
    collection: db?.[RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME],
  });

  const vtm = useReplicateVtm({
    db,
    collection: db?.[RXDB_VTM_COLLECTION_NAME],
  });

  const vmp = useReplicateVmp({
    db,
    collection: db?.[RXDB_VMP_COLLECTION_NAME],
  });

  const amp = useReplicateAmp({
    db,
    collection: db?.[RXDB_AMP_COLLECTION_NAME],
  });

  const nonPharmaItem = useReplicateNonPharmaItem({
    db,
    collection: db?.[RXDB_NON_PHARMA_ITEM_COLLECTION_NAME],
  });

  const clinicalProductForm = useReplicateClinicalProductForm({
    db,
    collection: db?.[RXDB_CLINICAL_PRODUCT_FORM_COLLECTION_NAME],
  });

  const clinicalProductCategory = useReplicateClinicalProductCategory({
    db,
    collection: db?.[RXDB_CLINICAL_PRODUCT_CATEGORY_COLLECTION_NAME],
  });

  const prescription = useReplicatePrescription({
    db,
    collection: db?.[RXDB_PRESCRIPTION_COLLECTION_NAME],
  });

  const dispensation = useReplicateDispensation({
    db,
    collection: db?.[RXDB_DISPENSATION_COLLECTION_NAME],
  });

  const consultation = useReplicateConsultation({
    db,
    collection: db?.[RXDB_CONSULTATION_COLLECTION_NAME],
  });

  const disease = useReplicateDisease({
    db,
    collection: db?.[RXDB_DISEASE_COLLECTION_NAME],
  });

  const symptom = useReplicateSymptom({
    db,
    collection: db?.[RXDB_SYMPTOM_COLLECTION_NAME],
  });

  const facility = useReplicateFacility({
    db,
    collection: db?.[RXDB_FACILITY_COLLECTION_NAME],
  });

  const imagingStudy = useReplicateImagingStudy({
    db,
    collection: db?.[RXDB_IMAGING_STUDY_COLLECTION_NAME],
  });

  const imagingInstance = useReplicateImagingInstance({
    db,
    collection: db?.[RXDB_IMAGING_INSTANCE_COLLECTION_NAME],
  });

  const referral = useReplicateReferral({
    db,
    collection: db?.[RXDB_REFERRAL_COLLECTION_NAME],
  });

  const referralFeedback = useReplicateReferralFeedback({
    db,
    collection: db?.[RXDB_REFERRAL_FEEDBACK_COLLECTION_NAME],
  });

  const epocusBilling = useReplicateEpocusBilling({
    db,
    collection: db?.[RXDB_EPOCUS_BILLING_COLLECTION_NAME],
  });

  const epocusEncounter = useReplicateEpocusEncounter({
    db,
    collection: db?.[RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME],
  });

  const epocusExam = useReplicateEpocusExam({
    db,
    collection: db?.[RXDB_EPOCUS_EXAM_COLLECTION_NAME],
  });

  const epocusPatientConsent = useReplicateEpocusPatientConsent({
    db,
    collection: db?.[RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME],
  });

  const epocusConsentDocument = useReplicateEpocusConsentDocument({
    db,
    collection: db?.[RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME],
  });

  const epocusProbeAssociation = useReplicateEpocusProbeAssociation({
    db,
    collection: db?.[RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME],
  });

  const epocusProgram = useReplicateEpocusProgram({
    db,
    collection: db?.[RXDB_EPOCUS_PROGRAM_COLLECTION_NAME],
  });

  const epocusReview = useReplicateEpocusReview({
    db,
    collection: db?.[RXDB_EPOCUS_REVIEW_COLLECTION_NAME],
  });

  const priceListFacility = useReplicatePriceListFacility({
    db,
    collection: db?.[RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME],
  });

  const price = useReplicatePrice({
    db,
    collection: db?.[RXDB_PRICE_COLLECTION_NAME],
  });

  const priceListTariff = useReplicatePriceListTariff({
    db,
    collection: db?.[RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME],
  });

  const priceListRevenueDepartment = useReplicatePriceListRevenueDepartment({
    db,
    collection: db?.[RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME],
  });

  const priceListItem = useReplicatePriceListItem({
    db,
    collection: db?.[RXDB_PRICE_LIST_ITEM_COLLECTION_NAME],
  });

  const priceListItemName = useReplicatePriceListItemName({
    db,
    collection: db?.[RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME],
  });

  const priceListItemExternalReference =
    useReplicatePriceListItemExternalReference({
      db,
      collection: db?.[RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME],
    });

  const invoice = useReplicateInvoice({
    db,
    collection: db?.[RXDB_INVOICE_COLLECTION_NAME],
  });

  const priceAcknowledgement = useReplicatePriceAcknowledgement({
    db,
    collection: db?.[RXDB_PRICE_ACKNOWLEDGEMENT_COLLECTION_NAME],
  });

  const notificationBellState = useReplicateNotificationBellState({
    db,
    collection: db?.[RXDB_NOTIFICATION_BELL_STATE_COLLECTION_NAME],
  });

  const notificationDismissal = useReplicateNotificationDismissal({
    db,
    collection: db?.[RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME],
  });

  const clinicInsurance = useReplicateClinicInsurance({
    db,
    collection: db?.[RXDB_CLINIC_INSURANCE_COLLECTION_NAME],
  });

  const exchangeRate = useReplicateExchangeRate({
    db,
    collection: db?.[RXDB_EXCHANGE_RATE_COLLECTION_NAME],
  });

  const hasUnsyncedChanges = !!(
    patient.earliestNonPushedChangeAt ||
    investigationActivity.earliestNonPushedChangeAt ||
    prescription.earliestNonPushedChangeAt ||
    dispensation.earliestNonPushedChangeAt ||
    consultation.earliestNonPushedChangeAt ||
    imagingStudy.earliestNonPushedChangeAt ||
    imagingInstance.earliestNonPushedChangeAt ||
    referral.earliestNonPushedChangeAt ||
    referralFeedback.earliestNonPushedChangeAt ||
    epocusBilling.earliestNonPushedChangeAt ||
    epocusEncounter.earliestNonPushedChangeAt ||
    epocusExam.earliestNonPushedChangeAt ||
    epocusProbeAssociation.earliestNonPushedChangeAt ||
    epocusReview.earliestNonPushedChangeAt ||
    invoice.earliestNonPushedChangeAt ||
    priceAcknowledgement.earliestNonPushedChangeAt ||
    notificationBellState.earliestNonPushedChangeAt ||
    notificationDismissal.earliestNonPushedChangeAt
  );

  return {
    patient,
    investigationActivity,
    investigationCatalogueItem,
    vtm,
    vmp,
    amp,
    nonPharmaItem,
    clinicalProductForm,
    clinicalProductCategory,
    prescription,
    dispensation,
    consultation,
    disease,
    symptom,
    facility,
    imagingStudy,
    imagingInstance,
    referral,
    "referral-feedback": referralFeedback,
    "epocus-billing": epocusBilling,
    epocusEncounter,
    epocusExam,
    epocusPatientConsent,
    epocusConsentDocument,
    epocusProbeAssociation,
    "epocus-program": epocusProgram,
    epocusReview,
    priceListFacility,
    price,
    priceListTariff,
    priceListRevenueDepartment,
    priceListItem,
    priceListItemName,
    priceListItemExternalReference,
    invoice,
    priceAcknowledgement,
    notification: { isPulling: false },
    notificationBellState,
    notificationDismissal,
    notificationPreference: { isPulling: false },
    "clinic-insurance": clinicInsurance,
    exchangeRate,
    "epocus-referral": { isPulling: false },
    "epocus-referral-outcome": { isPulling: false },
    hasUnsyncedChanges,
  };
};
